<template>
  <NuxtLayout
    name="point"
    :bread-crumb-list="breadcrumb"
  >
    <template #main>
      <div
        v-if="status === 'success'"
        class="main"
      >
        <PointWxTable
          :point-name-ja="data[pointId].point_ja"
          :point-id="pointId"
          :now="now"
          :page-title="title"
          @open-modal="showModal = true"
        />
        <ModalClothLegend
          v-if="showModal"
          @close-modal="showModal = false"
        />
      </div>
    </template>
    <template #main-bottom>
      <div
        v-if="status === 'success'"
        class="main"
      >
        <PointTempCompare
          :point-name-ja="data[pointId].point_ja"
          :point-id="pointId"
          :now="now"
          :cc="data[pointId].cc"
          @open-modal="showModal = true"
        />
        <PointMonthlyTemp
          v-if="data[pointId].normal_point_id"
          :point-name-ja="data[pointId].point_ja"
          :point-id="pointId"
        />
        <HorizontalStackAd
          v-if="!isWebViewFlag"
          left-ad-id="ads-btf2-left-pc"
          right-ad-id="ads-btf2-right-pc"
        />
        <div
          id="ads-btf1-sp"
          class="ads-btf1-sp"
        />
        <PointAirportWxTable
          v-if="data[pointId].airports"
          :point-id-list="data[pointId].airports"
          :now="now"
        />
        <PointSurroundingCityWxTable
          v-if="data[pointId].surroundings"
          :target-point-id="pointId"
          :point-id-list="data[pointId].surroundings"
          :now="now"
          target="city"
        />
        <PointSurroundingCityWxTable
          v-if="data[pointId].surroundings_landmark"
          :target-point-id="pointId"
          :point-id-list="data[pointId].surroundings_landmark"
          :now="now"
          target="landmark"
        />
        <HorizontalStackAd
          v-if="data[pointId].point_type === 'city' && !isWebViewFlag"
          left-ad-id="ads-btf4-left-pc"
          right-ad-id="ads-btf4-right-pc"
        />
        <div
          v-if="data[pointId].point_type === 'city'"
          id="ads-btf2-sp"
          class="ads-btf2-sp"
        />
        <RadarLinkCard
          :link="`/onebox/tenki/world/radar${pointId}`"
          :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/thumb/area_${data[pointId].area_id}/prec.webp`"
          :target-area="data[pointId].point_ja"
        />
        <CommonCountryList />
      </div>
    </template>
  </NuxtLayout>
</template>

<script setup>
import dayjs from 'dayjs'
import countryIdList from '~/data/table/country_id_list.json'

const route = useRoute()
const pointId = route.path
const areaId = route.params.area
const country = route.params.country
const { data, status } = await useFetch(
  `/api/point/info?pointId=${pointId}`,
  {
    key: `cityIndex:${pointId}`
  }
)
if (!data.value[pointId]) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Invalid pointId'
  })
}
const now = dayjs().unix()
const showModal = ref(false)
const runtimeConfig = useRuntimeConfig()
const { $isWebViewFlag: isWebViewFlag } = useNuxtApp()

const breadcrumb = [
  {
    text: '世界天気',
    link: `${runtimeConfig.public.hostDomain}/onebox/tenki/world/`
  },
  {
    text: `${areaLabelMap[data.value[pointId].area_id]}`,
    link: `${runtimeConfig.public.hostDomain}/onebox/tenki/world/${data.value[pointId].area_id}/`
  },
  {
    text: countryIdList[`/${areaId}/${country}/`].country_ja,
    link: `${runtimeConfig.public.hostDomain}/onebox/tenki/world/${areaId}/${country}/`
  },
  {
    text: `${data.value[pointId].point_ja}`
  }
]

const title = `${data.value[pointId].point_ja}の天気予報【${data.value[pointId].country_ja}】 - ウェザーニュース`
useCommonHead({
  title,
  description: `【${data.value[pointId].point_ja_description ?? data.value[pointId].point_ja}の2週間先までの天気予報】${data.value[pointId].point_ja_description_2 ?? data.value[pointId].point_ja_description ?? data.value[pointId].point_ja}の最高気温と最低気温を比較して旅行や出張に備えよう。雨雲レーダーは48時間先、国内外の空港の天気も確認できます。世界の天気に関するサポートは【ウェザーニュース】にお任せください。`,
  keywords: data.value[pointId].meta_kw ?? `${data.value[pointId].point_ja},天気,2週間`,
  appTitle: data.value[pointId].point_ja,
  appCountKey: pointId,
  iconUrl: `${runtimeConfig.public.gvsUrl}/onebox/img/world/icon/icon_world_white@2x.png`,
  iconGrayUrl: `${runtimeConfig.public.gvsUrl}/onebox/img/world/icon/icon_world_trans@2x.png`,
  iconSettingUrl: `${runtimeConfig.public.gvsUrl}/onebox/img/world/icon/icon_world_color@2x.png`
})

useCommonJsonld({
  title: '世界天気',
  description: '',
  breadcrumb: breadcrumb
})
watch(showModal, (value) => {
  if (value) {
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
  } else {
    document.documentElement.style.overflow = 'auto'
    document.body.style.overflow = 'auto'
  }
})
</script>

<style scoped>
.main {
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
}

.ads-btf1-sp {
  min-height: 50px;
  margin: 0px auto;
}
.ads-btf2-sp {
  min-height: 50px;
  margin: 0px auto;
}
</style>
